import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api, { getErrorMessage } from '@/services/api';
import { insurersService } from '@/services/insurers';
import { InsurerConnectionRequestsQueue } from '@/pages/country/InsurerConnectionRequestsQueue';

interface InsurerRelation {
  id: string;
  name: string;
  code: string;
  relation_status: string;
  /** The broker's most recent ask for this insurer, if it ever made one. */
  connection_request_id?: string | null;
  connection_request_status?: string;
  connection_request_note?: string;
}

export function BrokerInsurersManagePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [insurers, setInsurers] = useState<InsurerRelation[]>([]);
  const [brokerName, setBrokerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [insurersRes, brokerRes] = await Promise.all([
        api.get(`/brokers/${id}/assigned_insurers/`),
        api.get(`/brokers/${id}/`),
      ]);
      setInsurers(insurersRes.data);
      setBrokerName(brokerRes.data.name);
      setLoadError(null);
    } catch (error) {
      // Without this the table would render its "No insurers found" empty state
      // on a failed request, telling the admin this country has no insurance
      // companies when the truth is the request never landed.
      const message = getErrorMessage(error);
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, fetchData]);

  /** Assign or withdraw, and they are genuinely different calls now.
   *
   *  BOTH branches used to POST `assign_insurers`, so "remove" re-assigned the
   *  insurer it claimed to be removing and reported success. Withdrawing goes
   *  to `unassign_insurers`, which records SUSPENDED — not DISABLED_BY_BROKER,
   *  the one status the broker's own Enable button flips straight back. */
  const handleAssign = async (insurerId: string, assign: boolean) => {
    setBusy(insurerId);
    try {
      if (assign) {
        await api.post(`/brokers/${id}/assign_insurers/`, { insurer_ids: [insurerId] });
        toast.success('Insurer assigned');
      } else {
        await api.post(`/brokers/${id}/unassign_insurers/`, { insurer_ids: [insurerId] });
        toast.success('Insurer withdrawn');
      }
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  /** The verdict on a broker's connection request.
   *
   *  Approving is what creates the ACTIVE relation — the broker has no path to
   *  one of its own, which is the point of the request existing at all. */
  const handleDecide = async (
    requestId: string,
    insurerId: string,
    decision: 'approve' | 'decline',
  ) => {
    let reason = '';
    if (decision === 'decline') {
      reason = window.prompt('Why are you declining this request? (optional)') ?? '';
    }
    setBusy(insurerId);
    try {
      if (decision === 'approve') {
        await insurersService.approveInsurerConnectionRequest(requestId);
        toast.success('Connection approved');
      } else {
        await insurersService.declineInsurerConnectionRequest(requestId, reason);
        toast.success('Request declined');
      }
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const activeCount = insurers.filter((i) => i.relation_status === 'ACTIVE').length;
  const notConnected = insurers.filter((i) => i.relation_status === 'NOT_CONNECTED').length;
  const pendingRequests = insurers.filter((i) => i.connection_request_status === 'pending');

  const renderAction = (insurer: InsurerRelation) => {
    const working = busy === insurer.id;

    if (insurer.connection_request_status === 'pending' && insurer.connection_request_id) {
      return (
        <div className="inline-flex items-center gap-2">
          <button
            onClick={() => handleDecide(insurer.connection_request_id!, insurer.id, 'approve')}
            disabled={working}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {working ? 'Working...' : 'Approve'}
          </button>
          <button
            onClick={() => handleDecide(insurer.connection_request_id!, insurer.id, 'decline')}
            disabled={working}
            className="border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
          >
            Decline
          </button>
        </div>
      );
    }

    if (insurer.relation_status === 'ACTIVE') {
      return (
        <button
          onClick={() => handleAssign(insurer.id, false)}
          disabled={working}
          className="border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
        >
          {working ? 'Withdrawing...' : 'Withdraw'}
        </button>
      );
    }

    return (
      <button
        onClick={() => handleAssign(insurer.id, true)}
        disabled={working}
        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
      >
        {working
          ? 'Assigning...'
          : insurer.relation_status === 'NOT_CONNECTED'
          ? 'Assign'
          : 'Re-assign'}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/brokers/${id}`)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Manage Insurers</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Assign insurance companies to {brokerName || 'this broker'}
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Insurers</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{insurers.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Assigned</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{activeCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Not Assigned</p>
          <p className="text-2xl font-bold text-gray-500 dark:text-gray-400 mt-1">{notConnected}</p>
        </div>
        <div
          className={`rounded-lg shadow-sm border p-4 ${
            pendingRequests.length > 0
              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">Awaiting your approval</p>
          <p
            className={`text-2xl font-bold mt-1 ${
              pendingRequests.length > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-gray-400'
            }`}
          >
            {pendingRequests.length}
          </p>
        </div>
      </div>

      {/* THE QUEUE. The table below is this ONE broker; a request from any other
          broker in the country used to reach nobody, because the only screen
          that showed a pending request was the one belonging to the broker that
          filed it — and nothing pointed an admin at it. This panel is the
          country-wide view, and the only consumer of
          GET /brokers/insurer-connection-requests/. */}
      <InsurerConnectionRequestsQueue highlightBrokerId={id} onDecided={fetchData} />

      {/* Insurers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : loadError ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">Could not load insurers</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">{loadError}</p>
            <button
              onClick={fetchData}
              className="mt-4 inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Try again
            </button>
          </div>
        ) : insurers.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">No insurers found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No insurance companies are available in this country.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Insurer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {insurers.map((insurer) => (
                  <tr key={insurer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                          <BuildingOffice2Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{insurer.name}</p>
                          {insurer.connection_request_status === 'pending' &&
                            insurer.connection_request_note && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-md">
                                “{insurer.connection_request_note}”
                              </p>
                            )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{insurer.code}</td>
                    <td className="px-6 py-4">
                      {insurer.connection_request_status === 'pending' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                          Requested by broker
                        </span>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            insurer.relation_status === 'ACTIVE'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                              : insurer.relation_status === 'NOT_CONNECTED'
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                          }`}
                        >
                          {insurer.relation_status === 'ACTIVE'
                            ? 'Assigned'
                            : insurer.relation_status === 'NOT_CONNECTED'
                            ? 'Not Assigned'
                            : insurer.relation_status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">{renderAction(insurer)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
